import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, X, ChevronDown, Store, Heart, Package, MapPin, Navigation, Edit } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { btn } from '../../utils/buttonStyles';
import ListingCard from '../components/ListingCard';
import CategoryFilter from '../components/CategoryFilter';
import PriceFilter from '../components/PriceFilter';
import ConditionFilter from '../components/ConditionFilter';
import marketplaceAPI from '../services/marketplaceService';
import toast from 'react-hot-toast';

const MY_LISTINGS_STATUS_TABS = [
  { value: null, label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'sold', label: 'Sold' },
  { value: 'reserved', label: 'Reserved' },
];

export default function MarketplacePage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { location: userLocation, requestLocation, initialized: locationInitialized, isLoading: locationLoading } = useLocation();
  const { setBreadcrumbItems } = useBreadcrumb();
  const [searchParams, setSearchParams] = useSearchParams();

  // Main tab: 'browse' | 'my-listings' | 'favorites'
  const [activeTab, setActiveTab] = useState('browse');
  // Status sub-filter for My Listings tab
  const [myListingsStatus, setMyListingsStatus] = useState(null);

  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [savedListingIds, setSavedListingIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Near Me filter state
  const [nearMeEnabled, setNearMeEnabled] = useState(searchParams.get('nearMe') === 'true');
  const [radiusKm, setRadiusKm] = useState(parseInt(searchParams.get('radius')) || 50);
  // Auto-enable Nearby once when location first becomes available (user can still toggle it off)
  const nearMeAutoEnabledRef = useRef(false);

  // Filter state from URL params (search is client-side only, not in filters)
  const [filters, setFilters] = useState({
    categoryId: searchParams.get('category') || null,
    minPrice: searchParams.get('minPrice') || null,
    maxPrice: searchParams.get('maxPrice') || null,
    condition: searchParams.get('condition') || null,
    sortBy: searchParams.get('sortBy') || 'newest',
  });

  const [searchInput, setSearchInput] = useState('');
  // Debounced value sent to the API — avoids a request on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever the search query changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedQuery]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await marketplaceAPI.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Set breadcrumb
  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'Market Place' }
    ]);
    return () => {
      setBreadcrumbItems([]);
    };
  }, [setBreadcrumbItems]);

  // Load saved listings if user is logged in
  useEffect(() => {
    const loadSavedListings = async () => {
      if (!user) return;
      try {
        const saved = await marketplaceAPI.getSavedListings();
        const ids = new Set(saved.map((s) => s.listing?.id).filter(Boolean));
        setSavedListingIds(ids);
      } catch (error) {
        console.error('Failed to load saved listings:', error);
      }
    };
    loadSavedListings();
  }, [user]);

  // Derived: true when the browser has shared location coordinates
  const userLat = userLocation?.lat;
  const userLng = userLocation?.lng;
  const hasLocation = userLat != null && userLng != null;

  // Auto-enable Nearby the first time location becomes available
  useEffect(() => {
    if (hasLocation && !nearMeAutoEnabledRef.current) {
      nearMeAutoEnabledRef.current = true;
      setNearMeEnabled(true);
    }
  }, [hasLocation]);

  // true once permission status is known and any auto-fetch has completed —
  // prevents making a no-location call followed by a with-location call
  const locationReady = locationInitialized && !locationLoading;

  // Load listings — switches between browse (all) and my listings based on active tab
  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'my-listings') {
        if (!user) return;
        const myListings = await marketplaceAPI.getMyListings(myListingsStatus);
        setListings(myListings);
        setPagination((prev) => ({ ...prev, total: myListings.length, totalPages: 1 }));
        return;
      }

      if (activeTab === 'favorites') {
        if (!user) return;
        const saved = await marketplaceAPI.getSavedListings();
        const favListings = saved.filter((s) => s.listing).map((s) => ({ ...s.listing, savedAt: s.savedAt }));
        setListings(favListings);
        // Keep savedListingIds in sync
        setSavedListingIds(new Set(favListings.map((l) => l.id)));
        setPagination((prev) => ({ ...prev, total: favListings.length, totalPages: 1 }));
        return;
      }

      // Browse tab: always use /search so semantic query works with or without location
      let data;
      if (hasLocation || debouncedQuery) {
        const effectiveSortBy = nearMeEnabled
          ? 'distance'
          : debouncedQuery && filters.sortBy === 'newest'
          ? 'relevance'
          : filters.sortBy === 'newest' && hasLocation
          ? 'distance'
          : filters.sortBy;

        const params = {
          query: debouncedQuery || undefined,
          categoryId: filters.categoryId || undefined,
          minPrice: filters.minPrice || undefined,
          maxPrice: filters.maxPrice || undefined,
          condition: filters.condition || undefined,
          sortBy: effectiveSortBy,
          page: pagination.page,
          limit: pagination.limit,
          userLat: hasLocation ? userLat : undefined,
          userLng: hasLocation ? userLng : undefined,
          radiusKm: nearMeEnabled ? radiusKm : 0,
        };
        Object.keys(params).forEach((key) => {
          if (params[key] === undefined || params[key] === null) delete params[key];
        });
        data = await marketplaceAPI.searchListings(params);
      } else {
        const params = {
          ...filters,
          page: pagination.page,
          limit: pagination.limit,
        };
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === '') delete params[key];
        });
        data = await marketplaceAPI.getListings(params);
      }

      setListings(data.listings || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (error) {
      console.error('Failed to load listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [
    activeTab, myListingsStatus, user,
    filters, pagination.page, pagination.limit,
    nearMeEnabled, radiusKm, hasLocation, userLat, userLng,
    debouncedQuery,
  ]);

  // Only fire once location status is settled — avoids a no-location call followed
  // immediately by a with-location call when permission was previously granted.
  useEffect(() => {
    if (!locationReady) return;
    loadListings();
  }, [loadListings, locationReady]);


  // Update URL params when browse filters change
  useEffect(() => {
    if (activeTab !== 'browse') return;
    const params = new URLSearchParams();
    if (filters.categoryId) params.set('category', filters.categoryId);
    if (filters.minPrice) params.set('minPrice', filters.minPrice);
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
    if (filters.condition) params.set('condition', filters.condition);
    if (filters.sortBy && filters.sortBy !== 'newest') params.set('sortBy', filters.sortBy);
    if (nearMeEnabled) params.set('nearMe', 'true');
    if (nearMeEnabled && radiusKm !== 50) params.set('radius', radiusKm);
    setSearchParams(params);
  }, [activeTab, filters, nearMeEnabled, radiusKm, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePriceChange = ({ minPrice, maxPrice }) => {
    setFilters((prev) => ({ ...prev, minPrice, maxPrice }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSave = async (listingId, save) => {
    if (!user) {
      toast.error('Please sign in to save listings');
      return;
    }
    try {
      if (save) {
        await marketplaceAPI.saveListing(listingId);
        setSavedListingIds((prev) => new Set([...prev, listingId]));
        toast.success('Listing saved');
      } else {
        await marketplaceAPI.unsaveListing(listingId);
        setSavedListingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(listingId);
          return newSet;
        });
        if (activeTab === 'favorites') {
          setListings((prev) => prev.filter((l) => l.id !== listingId));
        }
        toast.success('Listing removed from saved');
      }
    } catch (error) {
      toast.error('Failed to update saved listings');
    }
  };

  const clearFilters = () => {
    setFilters({
      categoryId: null,
      minPrice: null,
      maxPrice: null,
      condition: null,
      sortBy: 'newest',
    });
    setSearchInput('');
    setNearMeEnabled(false);
    setRadiusKm(50);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleNearMeToggle = async () => {
    if (nearMeEnabled) {
      setNearMeEnabled(false);
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    if (!userLocation?.lat) {
      toast.loading('Requesting location access...', { id: 'location-request' });
      const success = await requestLocation();
      toast.dismiss('location-request');
      if (!success) {
        toast.error('Location access denied. Please enable location in your browser settings.');
        return;
      }
      toast.success('Location access granted!');
    }
    setNearMeEnabled(true);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchInput('');
    setListings([]);
    setPagination((prev) => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
  };

  const hasFilterPanelFilters =
    filters.categoryId || filters.minPrice || filters.maxPrice || filters.condition;

  const hasActiveFilters = hasFilterPanelFilters || debouncedQuery || nearMeEnabled;

  const isBrowse = activeTab === 'browse';
  const isMyListings = activeTab === 'my-listings';
  const isFavorites = activeTab === 'favorites';

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${theme.background} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-0 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className={`text-2xl font-bold ${theme.text}`}>Marketplace</h1>
            <div className="flex gap-2">
              {user && (
                <Link
                  to="/marketplace/listing/new"
                  className={`flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Sell Item</span>
                </Link>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className={`flex gap-1 mb-4 border-b ${theme.border}`}>
            <button
              onClick={() => handleTabChange('browse')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                isBrowse
                  ? 'border-blue-500 text-blue-500'
                  : `border-transparent ${theme.textSecondary} hover:${theme.text}`
              }`}
            >
              Browse
            </button>
            {user && (
              <button
                onClick={() => handleTabChange('my-listings')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
                  isMyListings
                    ? 'border-blue-500 text-blue-500'
                    : `border-transparent ${theme.textSecondary} hover:${theme.text}`
                }`}
              >
                <Package className="w-4 h-4" />
                My Listings
              </button>
            )}
            {user && (
              <button
                onClick={() => handleTabChange('favorites')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
                  isFavorites
                    ? 'border-blue-500 text-blue-500'
                    : `border-transparent ${theme.textSecondary} hover:${theme.text}`
                }`}
              >
                <Heart className="w-4 h-4" />
                Favorites
              </button>
            )}
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textSecondary}`} />
              <input
                type="text"
                placeholder={isBrowse ? 'Search sports gear...' : isFavorites ? 'Search favorites...' : 'Search your listings...'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${theme.border} ${theme.card} ${theme.text} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            {/* Browse-only controls */}
            {isBrowse && (
              <>
                <button
                  type="button"
                  onClick={handleNearMeToggle}
                  className={`px-4 py-2.5 rounded-lg border flex items-center gap-2 transition-colors ${
                    nearMeEnabled
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : `${theme.border} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  <Navigation className="w-5 h-5" />
                  <span className="hidden sm:inline">Nearby</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2.5 rounded-lg border ${theme.border} ${theme.text} flex items-center gap-2 ${theme.cardHover} ${
                    hasFilterPanelFilters ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filters</span>
                  {hasFilterPanelFilters && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </>
            )}
          </form>

          {/* Nearby Radius Selector */}
          {isBrowse && nearMeEnabled && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <label className={`text-sm ${theme.textSecondary}`}>Distance:</label>
              <select
                value={radiusKm}
                onChange={(e) => {
                  setRadiusKm(parseInt(e.target.value));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={`px-3 py-1.5 text-sm rounded-lg border ${theme.border} ${theme.card} ${theme.text} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value={10}>10 km</option>
                <option value={25}>25 km</option>
                <option value={50}>50 km</option>
                <option value={100}>100 km</option>
                <option value={200}>200 km</option>
              </select>
              {userLocation?.city && (
                <span className={`text-sm ${theme.textSecondary} flex items-center gap-1`}>
                  <MapPin className="w-4 h-4" />
                  {userLocation.city}, {userLocation.state}
                </span>
              )}
            </div>
          )}

          {/* My Listings status sub-filter */}
          {isMyListings && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {MY_LISTINGS_STATUS_TABS.map((tab) => (
                <button
                  key={tab.value ?? 'all'}
                  onClick={() => {
                    setMyListingsStatus(tab.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    myListingsStatus === tab.value
                      ? theme.primary
                      : `${theme.card} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Browse Filters Panel */}
        {isBrowse && showFilters && (
          <div className={`border-t ${theme.border}`}>
            <div className="max-w-7xl mx-auto px-4 lg:px-0 py-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-full sm:w-auto sm:min-w-[180px]">
                  <CategoryFilter
                    categories={categories}
                    selectedCategory={filters.categoryId}
                    onChange={(value) => handleFilterChange('categoryId', value)}
                  />
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px]">
                  <PriceFilter
                    minPrice={filters.minPrice}
                    maxPrice={filters.maxPrice}
                    onChange={handlePriceChange}
                  />
                </div>
                <div className="w-full sm:w-auto sm:min-w-[150px]">
                  <ConditionFilter
                    selectedCondition={filters.condition}
                    onChange={(value) => handleFilterChange('condition', value)}
                  />
                </div>
                <div className="w-full sm:w-auto sm:min-w-[180px]">
                  <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                    Sort By
                  </label>
                  <select
                    value={
                      nearMeEnabled || (hasLocation && filters.sortBy === 'newest')
                        ? 'distance'
                        : filters.sortBy
                    }
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    disabled={nearMeEnabled}
                    className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.card} ${theme.text} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60`}
                  >
                    {debouncedQuery && <option value="relevance">Best Match</option>}
                    {(nearMeEnabled || hasLocation) && <option value="distance">Nearest First</option>}
                    <option value="newest">Newest First</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                  </select>
                </div>
                {hasFilterPanelFilters && (
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 text-sm rounded-lg border ${theme.border} ${theme.text} ${theme.cardHover} flex items-center gap-1.5 self-end`}
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Results count */}
        <div className={`mb-4 text-sm ${theme.textSecondary}`}>
          {loading ? (
            'Loading...'
          ) : debouncedQuery ? (
            `${listings.length} ${listings.length === 1 ? 'result' : 'results'} for "${debouncedQuery}"`
          ) : isMyListings || isFavorites ? (
            `${listings.length} ${listings.length === 1 ? 'listing' : 'listings'}`
          ) : (
            `${pagination.total} ${pagination.total === 1 ? 'listing' : 'listings'} found`
          )}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`rounded-lg ${theme.card} animate-pulse`}>
                <div className={`h-48 ${theme.backgroundSecondary} rounded-t-lg`} />
                <div className="p-4 space-y-3">
                  <div className={`h-4 ${theme.backgroundSecondary} rounded w-3/4`} />
                  <div className={`h-6 ${theme.backgroundSecondary} rounded w-1/2`} />
                  <div className={`h-4 ${theme.backgroundSecondary} rounded w-full`} />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className={`text-center py-16 ${theme.card} rounded-lg`}>
            {isMyListings ? (
              <>
                <Package className={`w-16 h-16 mx-auto mb-4 ${theme.textSecondary}`} />
                <h3 className={`text-lg font-medium mb-2 ${theme.text}`}>No listings yet</h3>
                <p className={`mb-4 ${theme.textSecondary}`}>Start selling your sports gear!</p>
                <Link
                  to="/marketplace/listing/new"
                  className={`inline-flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                >
                  <Plus className="w-5 h-5" />
                  Create Listing
                </Link>
              </>
            ) : isFavorites ? (
              <>
                <Heart className={`w-16 h-16 mx-auto mb-4 ${theme.textSecondary}`} />
                <h3 className={`text-lg font-medium mb-2 ${theme.text}`}>No favorites yet</h3>
                <p className={`mb-4 ${theme.textSecondary}`}>Tap the heart on any listing to save it here.</p>
                <button
                  onClick={() => handleTabChange('browse')}
                  className={`inline-flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                >
                  <Store className="w-5 h-5" />
                  Browse Listings
                </button>
              </>
            ) : (
              <>
                <Store className={`w-16 h-16 mx-auto mb-4 ${theme.textSecondary}`} />
                <h3 className={`text-lg font-medium mb-2 ${theme.text}`}>No listings found</h3>
                <p className={`mb-4 ${theme.textSecondary}`}>
                  {nearMeEnabled
                    ? `No listings found within ${radiusKm}km of your location.`
                    : hasActiveFilters
                    ? 'Try adjusting your filters'
                    : 'Be the first to list your sports gear!'}
                </p>
                {nearMeEnabled && (
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => setRadiusKm(200)}
                      className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover}`}
                    >
                      Expand to 200km
                    </button>
                    <button
                      onClick={() => setNearMeEnabled(false)}
                      className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                    >
                      Search Everywhere
                    </button>
                  </div>
                )}
                {user && !hasActiveFilters && !nearMeEnabled && (
                  <Link
                    to="/marketplace/listing/new"
                    className={`inline-flex items-center gap-2 mt-4 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                  >
                    <Plus className="w-5 h-5" />
                    Create Listing
                  </Link>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {listings.map((listing) =>
                isMyListings ? (
                  /* My Listings card — same card with an Edit button overlay */
                  <div key={listing.id} className="relative">
                    <ListingCard
                      listing={listing}
                      isSaved={savedListingIds.has(listing.id)}
                      onSave={handleSave}
                    />
                    <Link
                      to={`/marketplace/listing/${listing.id}/edit`}
                      onClick={(e) => e.stopPropagation()}
                      className={`absolute bottom-[72px] right-2 flex items-center gap-1 ${btn.base} ${btn.sm} ${theme.buttonPrimary} shadow`}
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </Link>
                  </div>
                ) : (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    isSaved={savedListingIds.has(listing.id)}
                    onSave={handleSave}
                  />
                )
              )}
            </div>

            {/* Pagination (Browse only — My Listings returns all) */}
            {isBrowse && pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`px-4 py-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-50 disabled:cursor-not-allowed ${theme.cardHover}`}
                >
                  Previous
                </button>
                <span className={`px-4 py-2 ${theme.text}`}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  className={`px-4 py-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-50 disabled:cursor-not-allowed ${theme.cardHover}`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
